import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../../../components/controls/FormInput";
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/20/solid";
import Autocomplete from "../../../components/Places/PlacesAutocomplete";
import ImageUploader from "../../../components/controls/ImageUploader";
import { PlusIcon } from "@heroicons/react/24/outline";
import UserRolesSection from "./UserRolesSection";
import Modal from "../../../components/controls/Modal";
import Button from "../../../components/controls/Button";
import Tabs from "../../../components/controls/Tabs";

export default function AdminUserModal({
  isOpen,
  onClose,
  onSave,
  user,
  availableRoles = [],
  currentUserId = "",
}) {
  const { t } = useTranslation();
  const isEditing = !!user;

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [isProvider, setIsProvider] = useState(false);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyIndex, setActiveCompanyIndex] = useState(0);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [initialRoleIds, setInitialRoleIds] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setFullName(user.fullName || "");
      setIsProvider(user.isProvider || false);
      setIsGoogleAccount(user?.isGoogleAccount || false);
      setCompanies(user.companies || []);
      setSelectedRoleIds(user.userRoles?.map((r) => r.roleId) || []);
      if (user?.userRoles) {
        const ids = user.userRoles.map((r) => r.roleId);
        setSelectedRoleIds(ids);
        setInitialRoleIds(ids);
      }
    } else {
      setEmail("");
      setFullName("");
      setIsProvider(false);
      setIsGoogleAccount(false);
      setCompanies([]);
      setSelectedRoleIds([]);
    }
    setActiveTab(0);
  }, [user]);

  const handleClose = () => {
    setEmail("");
    setFullName("");
    setIsProvider(false);
    setIsGoogleAccount(false);
    setCompanies([]);
    setActiveTab(0);
    onClose();
  };

  const addCompany = () => {
    const newCompany = {
      name: "",
      neqNumber: "",
      address: "",
      phoneNumber: "",
      contactPerson: "",
      website: "",
      contactEmail: "",
      imageUrl: "",
      subscriptionTier: 1,
      userId: user?.id || null,
      location: null,
      tempImageUrl: "",
      isActive: true,
    };
    setCompanies([...companies, newCompany]);
    setActiveCompanyIndex(companies.length);
  };

  const handleSave = async () => {
    const roleDiff = selectedRoleIds
      .filter((id) => !initialRoleIds.includes(id))
      .concat(initialRoleIds.filter((id) => !selectedRoleIds.includes(id)));

    if (roleDiff.length > 0) {
      const changedDescriptions = availableRoles
        .filter((r) => roleDiff.includes(r.id))
        .map((r) => r.description || r.name);

      const confirmText =
        "Les rôles suivants ont été modifiés, enregistrer ?\n\n- " +
        changedDescriptions.join("\n- ");

      const confirmed = window.confirm(confirmText);
      if (!confirmed) return;
    }

    const payload = {
      id: user?.id || null,
      email,
      fullName,
      isProvider,
      isGoogleAccount,
      companies: companies.map((company) => ({
        ...company,
        imageUrl: company.hasChangedImageUrl
          ? company.tempImageUrl ?? null
          : company.imageUrl,
      })),
      userRoles: selectedRoleIds.map((roleId) => ({
        roleId,
        userId: user?.id || null,
      })),
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/User`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": currentUserId || "",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();

        if (errorText.includes("UserAlreadyExists")) {
          alert("Un utilisateur existe déjà avec cet email.");
        } else {
          alert("Une erreur est survenue : " + errorText);
        }

        throw new Error(errorText);
      }

      onClose();
      onSave(payload);
    } catch (error) {}
  };

  const companyIsActiveToggle = (activeCompanyIndex) => (
    <div className="flex items-center gap-4 mt-4">
      <label className="flex items-center gap-3 cursor-pointer">
        <span
          className={`font-semibold ${
            companies[activeCompanyIndex].isActive
              ? "text-green-600"
              : "text-red-500"
          }`}
        >
          {companies[activeCompanyIndex].isActive
            ? t("adminUserTable.companyActive")
            : t("adminUserTable.companyInactive")}
        </span>
        <input
          type="checkbox"
          className="checkbox-pro"
          checked={companies[activeCompanyIndex].isActive}
          onChange={(e) => {
            const newValue = e.target.checked;
            if (!newValue) {
              const confirmed = window.confirm(
                t("adminUserTable.confirmDeactivate")
              );
              if (!confirmed) return;
            }

            const updated = [...companies];
            updated[activeCompanyIndex].isActive = newValue;
            setCompanies(updated);
          }}
        />
      </label>
    </div>
  );

  const tabs = [
    { id: 0, label: t("adminUserTable.generalTab") },
    { id: 1, label: t("adminUserTable.companyTab") },
    { id: 2, label: t("Roles") },
  ];

  const footer = (
    <div className="flex gap-3 justify-end">
      <button
        type="button"
        className="px-4 py-2 bg-gray-100 text-gray-900 rounded-full hover:bg-gray-200 transition font-medium"
        onClick={handleClose}
      >
        {t("adminUserTable.cancel")}
      </button>
      <button
        type="button"
        className="px-4 py-2 bg-primary text-white rounded-full hover:opacity-90 transition font-medium"
        onClick={handleSave}
      >
        {t("adminUserTable.save")}
      </button>
    </div>
  );

  const RenderCompanyTab = () => {
    if (activeTab !== 1) return null;

    return (
      <div>
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            onClick={addCompany}
            color="primary"
            size="sm"
            leftIcon={<PlusIcon className="w-5 h-5" />}
          >
            {t("adminUserTable.addCompany")}
          </Button>
        </div>

        {companies.length > 1 && (
          <Tabs
            tabs={companies.map((c, i) => ({
              key: i,
              label: c.name?.trim() || t("adminUserTable.newCompany"),
            }))}
            activeIndex={activeCompanyIndex}
            onChange={setActiveCompanyIndex}
          />
        )}

        {companies.length === 0 ? (
          <p className="text-center text-gray-500 italic py-8">
            {t("adminUserTable.noCompany")}
          </p>
        ) : (
          <div className="w-full border border-ananas p-4 rounded-b-lg rounded-r-lg bg-white">
            <div className="flex flex-col lg:flex-row gap-4 items-start">
              <div className="w-full lg:w-2/3 space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.companyNameLabel")}
                      value={companies[activeCompanyIndex].name}
                      placeholder={t("becomeProvider.companyNamePlaceholder")}
                      onChange={(name, value) => {
                        const updated = [...companies];
                        updated[activeCompanyIndex].name = value;
                        setCompanies(updated);
                      }}
                      required
                      icon={
                        <BuildingOfficeIcon className="w-5 h-5 text-gray-500" />
                      }
                    />
                  </div>

                  <div className="w-full sm:w-1/2">
                    <Autocomplete
                      onSelect={(e) => {
                        const updated = [...companies];
                        updated[activeCompanyIndex].address =
                          e.formattedAddress;
                        updated[activeCompanyIndex].location = e;
                        setCompanies(updated);
                      }}
                      initialAddress={
                        companies[activeCompanyIndex].address || ""
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.contactEmailLabel")}
                      value={companies[activeCompanyIndex].contactEmail || ""}
                      placeholder={t("becomeProvider.contactEmailPlaceholder")}
                      onChange={(name, value) => {
                        const updated = [...companies];
                        updated[activeCompanyIndex].contactEmail = value;
                        setCompanies(updated);
                      }}
                      required
                    />
                  </div>

                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.phoneNumberLabel")}
                      value={companies[activeCompanyIndex].phoneNumber}
                      placeholder={t("becomeProvider.phoneNumberPlaceholder")}
                      onChange={(name, value) => {
                        const updated = [...companies];
                        updated[activeCompanyIndex].phoneNumber = value;
                        setCompanies(updated);
                      }}
                      icon={<PhoneIcon className="w-5 h-5 text-gray-500" />}
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.contactPersonLabel")}
                      value={companies[activeCompanyIndex].contactPerson}
                      placeholder={t("becomeProvider.contactPersonPlaceholder")}
                      onChange={(name, value) => {
                        const updated = [...companies];
                        updated[activeCompanyIndex].contactPerson = value;
                        setCompanies(updated);
                      }}
                      icon={
                        <UserCircleIcon className="w-5 h-5 text-gray-500" />
                      }
                      required
                    />
                  </div>

                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.neqNumberLabel")}
                      value={companies[activeCompanyIndex].neqNumber}
                      placeholder={t("becomeProvider.neqNumberPlaceholder")}
                      onChange={(name, value) => {
                        const updated = [...companies];
                        updated[activeCompanyIndex].neqNumber = value;
                        setCompanies(updated);
                      }}
                      icon={
                        <IdentificationIcon className="w-5 h-5 text-gray-500" />
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-1/2">
                    <FormInput
                      label={t("becomeProvider.websiteLabel")}
                      value={companies[activeCompanyIndex].website}
                      placeholder={t("becomeProvider.websitePlaceholder")}
                      onChange={(name, value) => {
                        const updated = [...companies];
                        updated[activeCompanyIndex].website = value;
                        setCompanies(updated);
                      }}
                      icon={<GlobeAltIcon className="w-5 h-5 text-gray-500" />}
                    />
                  </div>

                  {companyIsActiveToggle(activeCompanyIndex)}
                </div>
              </div>

              <div className="w-full lg:w-1/3 lg:sticky lg:top-4 self-start space-y-4">
                <div className="max-h-[520px] overflow-auto">
                  <ImageUploader
                    ratio="square"
                    initialImageUrl={
                      companies[activeCompanyIndex]?.tempImageUrl ||
                      companies[activeCompanyIndex]?.imageUrl ||
                      ""
                    }
                    onTempImageChange={(url) => {
                      const updated = [...companies];
                      updated[activeCompanyIndex].tempImageUrl = url?.trim()
                        ? url
                        : null;
                      updated[activeCompanyIndex].hasChangedImageUrl = true;
                      setCompanies(updated);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isEditing ? t("adminUserTable.editTitle") : t("adminUserTable.addTitle")
      }
      footer={footer}
      size="xl"
      showCloseButton
    >
      {/* Tabs Navigation */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium transition ${
              activeTab === tab.id
                ? "border-b-2 border-primary text-primary"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {/* General Tab */}
        {activeTab === 0 && (
          <div className="space-y-4">
            <FormInput
              label={t("adminUserTable.email")}
              name="email"
              value={email}
              onChange={(name, value) => setEmail(value)}
              required
            />
            <FormInput
              label={t("adminUserTable.fullName")}
              name="fullName"
              value={fullName}
              onChange={(name, value) => setFullName(value)}
            />
            <div className="hidden">
              <label className="flex items-center gap-3 cursor-pointer">
                <span className="font-medium">
                  {t("adminUserTable.isProvider")}
                </span>
                <input
                  type="checkbox"
                  className="toggle"
                  checked={isProvider}
                  onChange={() => setIsProvider(!isProvider)}
                />
              </label>
            </div>
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <span className="font-medium text-primary">
                  Compte Google ?
                </span>
                <input
                  type="checkbox"
                  className="toggle"
                  checked={isGoogleAccount}
                  onChange={(e) => setIsGoogleAccount(e.target.checked)}
                />
              </label>
            </div>
          </div>
        )}
        {RenderCompanyTab()}

        {/* Roles Tab */}
        {activeTab === 2 && (
          <div className="min-h-[400px]">
            <UserRolesSection
              values={selectedRoleIds}
              onChange={setSelectedRoleIds}
              availableRoles={availableRoles}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
